/**
 * Slice 2 — serialize the editor's live state into the on-disk source
 * format the server renders (T4.6):
 *
 *   site.json        ← { site, edges }
 *   pages/<id>.json  ← one Page per sitemap node
 *
 * This bridges the editor's data model (xyflow nodes + a frontninja2
 * gridStore + per-widget contentProps) onto the SiteDoc schema. The
 * two diverge, so the mapping fills the editor's current gaps with
 * documented, deterministic defaults:
 *
 *   - Page slug: the editor has no per-page slug field yet. The home
 *     page (first node with no incoming edge, else the first node) gets
 *     "/"; others get "/" + slugify(label), de-duplicated. Replace this
 *     with a real per-page slug field when the editor grows one.
 *   - Page title ← node label. Page SEO ← empty (no SEO editor yet).
 *   - Theme ← caller-supplied, else the "minimal" preset (no theme
 *     picker yet).
 *   - Image asset: the editor stores the upload URL in contentProps;
 *     the sha256 is recovered from it (key shape `<site>/<sha256>.ext`).
 *
 * Output is structurally typed against @website-builder/schemas but NOT
 * runtime-decoded: editor ids (node/item ids like "n1-welcome") are not
 * UUIDs, so the schema's branded-UUID decode would reject them. The
 * server renders structurally (also type-only), so non-UUID ids are
 * fine end to end. Serialization is deterministic — grid items sort by
 * (y, x, id) — so identical editor state yields byte-identical files.
 */
import type {
  SiteDoc,
  Site,
  Page,
  Edge,
  Theme,
  SiteThemeOrLegacy,
  GridWidgetContent,
  GridItem,
  Widget,
} from "@website-builder/schemas";
import * as Schema from "effect/Schema";
import * as Either from "effect/Either";
import { TreeFormatter } from "effect/ParseResult";
import {
  SiteDoc as SiteDocSchema,
  FormConfig,
  FieldName,
  InputIcon,
  EDITOR_ROW_HEIGHT_PX,
} from "@website-builder/schemas";
import type { GridState } from "@fredericrous/grid";
import { gapOf } from "@fredericrous/grid";

type GridCell = GridState["items"][string];

export interface EditorNode {
  readonly id: string;
  readonly position: { readonly x: number; readonly y: number };
  readonly label: string;
  /** URL-slug override (⋯ "Change URL…"); absent → derived from the label. */
  readonly slug?: string;
}

export interface EditorEdge {
  readonly id: string;
  readonly source: string;
  readonly target: string;
  readonly label?: string;
  readonly menuPosition?: number;
  readonly hidden?: boolean;
}

/** A cell's rectangle in grid units (col/row start + span). */
export interface GridRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Per-grid responsive layout, resolved from the Yjs doc (not the breakpoint-
 * tainted store). `desktop` is each item's base rectangle; `tablet`/`mobile` its
 * resolved per-tier rectangles (override → tier default → base); `hidden` the
 * per-tier hide sets. The publish flow supplies this (y-grid's
 * `resolveGridLayouts`); absent → the store positions stand for desktop and
 * there's no responsive layer.
 */
export type GridLayoutResolver = (
  gridId: string,
  columns: number,
) => {
  desktop: Readonly<Record<string, GridRect>>;
  tablet: Readonly<Record<string, GridRect>>;
  mobile: Readonly<Record<string, GridRect>>;
  hidden: {
    tablet: Readonly<Record<string, true>>;
    mobile: Readonly<Record<string, true>>;
  };
};

export interface SerializeInput {
  readonly siteId: string;
  readonly siteName: string;
  readonly description?: string;
  readonly theme?: SiteThemeOrLegacy;
  /** Opt the published menu into the hierarchical renderer (default flat). */
  readonly navNested?: boolean;
  readonly nodes: ReadonlyArray<EditorNode>;
  readonly edges: ReadonlyArray<EditorEdge>;
  readonly grids: Readonly<Record<string, GridState>>;
  /** Resolves each grid's desktop/tablet/mobile/hidden layout from the doc. */
  readonly resolveLayouts?: GridLayoutResolver;
}

export interface SerializedFile {
  readonly path: string;
  readonly content: string;
}

/** A problem that makes the site unpublishable until the author fixes it. */
export interface PublishError {
  /** The widget or section to fix. */
  readonly itemId: string;
  readonly message: string;
}

export interface SerializeResult {
  readonly doc: SiteDoc;
  readonly files: ReadonlyArray<SerializedFile>;
  /** Non-fatal mapping issues (omitted widgets, missing grids). */
  readonly warnings: ReadonlyArray<string>;
  /** Blocking: publishing refuses while any exist (forms that would send
   *  visitors or data somewhere wrong). */
  readonly errors: ReadonlyArray<PublishError>;
}

const DEFAULT_THEME: Theme = { preset: "minimal", knobs: {} };

// The singleton shared-region grid ids (mirror editor's sharedRegions.ts).
const HEADER_GRID_ID = "__header";
const FOOTER_GRID_ID = "__footer";

// A minimal valid Lexical EditorState — serializeToHtml on this emits
// "". Used when a text widget has no authored content yet.
const EMPTY_EDITOR_STATE = {
  root: {
    type: "root",
    format: "",
    indent: 0,
    version: 1,
    direction: null,
    children: [],
  },
};

function slugify(label: string): string {
  const s = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return s || "page";
}

function extractSha256(url: string): string | undefined {
  const m = url.match(/[0-9a-f]{64}/);
  return m ? m[0] : undefined;
}

/** A stored heading/button alignment Duro understands. */
const isAlign = (v: unknown): v is "start" | "center" | "end" =>
  v === "start" || v === "center" || v === "end";

function emptyGrid(columns = 12): GridWidgetContent {
  return { contentType: "grid", columns, items: [] };
}

function buildContent(
  item: GridCell,
  grids: Readonly<Record<string, GridState>>,
  warnings: string[],
  errors: PublishError[],
  resolve?: GridLayoutResolver,
): Widget["content"] | null {
  const cp = item.contentProps ?? {};
  switch (item.contentType) {
    case "text":
    case "editor": {
      // TextEditorWidget persists lexicalState as a JSON *string*
      // (JSON.stringify(editorState)); parse it back to the object the
      // renderer expects. Tolerate an already-parsed object too. Falls
      // back to an empty doc when absent/garbled.
      let ls: unknown = cp["lexicalState"];
      if (typeof ls === "string") {
        try {
          ls = JSON.parse(ls);
        } catch {
          ls = null;
        }
      }
      const editorState =
        ls && typeof ls === "object" && "root" in (ls as object)
          ? ls
          : EMPTY_EDITOR_STATE;
      return {
        contentType: item.contentType,
        editorState,
      } as unknown as Widget["content"];
    }
    case "image": {
      const url = typeof cp["url"] === "string" ? cp["url"] : "";
      const sha =
        extractSha256(url) ??
        (typeof cp["sha256"] === "string" ? cp["sha256"] : undefined);
      // No asset yet → publish on-brand placeholder art (seeded by the item
      // id so each slot differs) rather than a hole in the layout.
      const content: Record<string, unknown> = sha
        ? { contentType: "image", asset: sha }
        : { contentType: "image", placeholder: "brand", seed: item.id };
      if (typeof cp["alt"] === "string") content["alt"] = cp["alt"];
      if (typeof cp["caption"] === "string") content["caption"] = cp["caption"];
      // Crop-by-pan framing (Phase 4): carry object-position so the published
      // site shows the same crop as the editor. Default "50% 50%" is the CSS
      // default, so only emit a non-default one.
      if (
        typeof cp["objectPosition"] === "string" &&
        cp["objectPosition"] !== "50% 50%"
      )
        content["objectPosition"] = cp["objectPosition"];
      return content as unknown as Widget["content"];
    }
    case "video": {
      const url = typeof cp["url"] === "string" ? cp["url"] : "";
      const sha =
        extractSha256(url) ??
        (typeof cp["sha256"] === "string" ? cp["sha256"] : undefined);
      if (!sha) {
        warnings.push(
          `video widget ${item.id}: no sha256 resolvable from url ${JSON.stringify(url)}; omitted`,
        );
        return null;
      }
      const content: Record<string, unknown> = {
        contentType: "video",
        asset: sha,
        mime: typeof cp["mime"] === "string" ? cp["mime"] : "video/mp4",
      };
      // controls default on — only carry the non-default (off) + opt-in flags.
      if (cp["controls"] === false) content["controls"] = false;
      if (cp["autoplay"]) content["autoplay"] = true;
      if (cp["loop"]) content["loop"] = true;
      if (cp["muted"]) content["muted"] = true;
      return content as unknown as Widget["content"];
    }
    case "audio": {
      const url = typeof cp["url"] === "string" ? cp["url"] : "";
      const sha =
        extractSha256(url) ??
        (typeof cp["sha256"] === "string" ? cp["sha256"] : undefined);
      if (!sha) {
        warnings.push(
          `audio widget ${item.id}: no sha256 resolvable from url ${JSON.stringify(url)}; omitted`,
        );
        return null;
      }
      const content: Record<string, unknown> = {
        contentType: "audio",
        asset: sha,
        mime: typeof cp["mime"] === "string" ? cp["mime"] : "audio/mpeg",
      };
      // Precomputed waveform envelope + length → the published page draws the
      // waveform with no client-side decode. Ints only (guard against garbage).
      if (Array.isArray(cp["peaks"])) {
        const peaks = (cp["peaks"] as unknown[]).filter(
          (n): n is number => typeof n === "number" && Number.isFinite(n),
        );
        if (peaks.length > 0) content["peaks"] = peaks;
      }
      if (typeof cp["duration"] === "number" && cp["duration"] > 0)
        content["duration"] = cp["duration"];
      return content as unknown as Widget["content"];
    }
    case "form": {
      const fields = Array.isArray(cp["fields"]) ? cp["fields"] : [];
      const content: Record<string, unknown> = {
        contentType: "form",
        fields,
        submitButtonLabel:
          typeof cp["submitButtonLabel"] === "string"
            ? cp["submitButtonLabel"]
            : "Send",
      };
      if (typeof cp["deliveryEmail"] === "string") {
        content["deliveryEmail"] = cp["deliveryEmail"];
      }
      return content as unknown as Widget["content"];
    }
    case "button": {
      const content: Record<string, unknown> = {
        contentType: "button",
        label: typeof cp["label"] === "string" ? cp["label"] : "Button",
      };
      if (typeof cp["href"] === "string" && cp["href"].trim() !== "") {
        const href = publishableHref(cp["href"], item.id, errors);
        if (href !== null) content["href"] = href;
      }
      if (
        cp["variant"] === "primary" ||
        cp["variant"] === "secondary" ||
        cp["variant"] === "inverseSecondary" ||
        cp["variant"] === "link" ||
        cp["variant"] === "danger"
      ) {
        content["variant"] = cp["variant"];
      }
      if (cp["size"] === "default" || cp["size"] === "small") {
        content["size"] = cp["size"];
      }
      if (isAlign(cp["align"])) content["align"] = cp["align"];
      if (cp["type"] === "submit" || cp["type"] === "link")
        content["type"] = cp["type"];
      if (cp["width"] === "hug" || cp["width"] === "fill")
        content["width"] = cp["width"];
      if (
        cp["valign"] === "top" ||
        cp["valign"] === "middle" ||
        cp["valign"] === "bottom"
      )
        content["valign"] = cp["valign"];
      return content as unknown as Widget["content"];
    }
    case "heading": {
      const content: Record<string, unknown> = {
        contentType: "heading",
        text: typeof cp["text"] === "string" ? cp["text"] : "",
      };
      if (
        typeof cp["level"] === "number" &&
        cp["level"] >= 1 &&
        cp["level"] <= 6
      ) {
        content["level"] = cp["level"];
      }
      if (isAlign(cp["align"])) content["align"] = cp["align"];
      return content as unknown as Widget["content"];
    }
    case "card": {
      const content: Record<string, unknown> = { contentType: "card" };
      if (typeof cp["title"] === "string") content["title"] = cp["title"];
      if (typeof cp["body"] === "string") content["body"] = cp["body"];
      if (
        cp["variant"] === "elevated" ||
        cp["variant"] === "outlined" ||
        cp["variant"] === "filled" ||
        cp["variant"] === "interactive"
      ) {
        content["variant"] = cp["variant"];
      }
      if (
        cp["size"] === "default" ||
        cp["size"] === "compact" ||
        cp["size"] === "full"
      ) {
        content["size"] = cp["size"];
      }
      return content as unknown as Widget["content"];
    }
    case "nav": {
      // The page-links menu — a marker; the renderer builds the actual links
      // from the site's pages (per-page aria-current), so nothing to persist.
      return { contentType: "nav" };
    }
    case "spacer": {
      // Pure layout whitespace — a marker with no content. Kept (not dropped as
      // "unknown") so its grid cell reserves the space; renderPage emits an
      // empty cell for it.
      return { contentType: "spacer" };
    }
    case "input": {
      const content: Record<string, unknown> = { contentType: "input" };
      if (Either.isRight(Schema.decodeUnknownEither(FieldName)(cp["name"])))
        content["name"] = cp["name"];
      else
        errors.push({
          itemId: item.id,
          message:
            "This input needs a field name (a letter, then letters, digits or _) — it is what the form submits.",
        });
      if (typeof cp["label"] === "string" && cp["label"].trim() !== "")
        content["label"] = cp["label"];
      else
        errors.push({
          itemId: item.id,
          message:
            "This input needs a label: it is how screen readers announce it.",
        });
      if (typeof cp["placeholder"] === "string")
        content["placeholder"] = cp["placeholder"];
      if (
        cp["type"] === "text" ||
        cp["type"] === "search" ||
        cp["type"] === "email" ||
        cp["type"] === "url" ||
        cp["type"] === "tel"
      )
        content["type"] = cp["type"];
      if (cp["required"] === true) content["required"] = true;
      if (isIcon(cp["icon"])) content["icon"] = cp["icon"];
      return content as unknown as Widget["content"];
    }
    case "icon": {
      const content: Record<string, unknown> = { contentType: "icon" };
      if (isIcon(cp["name"])) content["name"] = cp["name"];
      else
        errors.push({
          itemId: item.id,
          message: `This icon has no glyph from the design system's set (${InputIcon.literals.join(", ")}).`,
        });
      if (cp["size"] === "sm" || cp["size"] === "md" || cp["size"] === "lg")
        content["size"] = cp["size"];
      if (typeof cp["label"] === "string" && cp["label"].trim() !== "")
        content["label"] = cp["label"];
      if (isAlign(cp["align"])) content["align"] = cp["align"];
      return content as unknown as Widget["content"];
    }
    case "inline-grid": {
      const nestedId = item.nestedGridId;
      const nested = nestedId ? grids[nestedId] : undefined;
      if (!nested || !nestedId) {
        warnings.push(
          `inline-grid widget ${item.id}: nestedGridId ${JSON.stringify(item.nestedGridId)} not found; rendered empty`,
        );
        return {
          ...emptyGrid(),
          contentType: "inline-grid",
          nestedGridId: nestedId ?? "",
          justify: "start",
        } as unknown as Widget["content"];
      }
      const built = buildGrid(
        nestedId,
        nested,
        grids,
        warnings,
        errors,
        resolve,
      );
      return {
        ...built,
        contentType: "inline-grid",
        nestedGridId: nestedId,
        justify: isAlign(cp["justify"]) ? cp["justify"] : "start",
      } as unknown as Widget["content"];
    }
    case "grid": {
      const nestedId = item.nestedGridId;
      const nested = nestedId ? grids[nestedId] : undefined;
      if (!nested || !nestedId) {
        warnings.push(
          `grid widget ${item.id}: nestedGridId ${JSON.stringify(item.nestedGridId)} not found; rendered empty`,
        );
        return emptyGrid();
      }
      const built = buildGrid(
        nestedId,
        nested,
        grids,
        warnings,
        errors,
        resolve,
      );
      // A section may be a form: the form (not any field) owns the action.
      if (cp["form"] === undefined) return built;
      const form = Schema.decodeUnknownEither(FormConfig)(cp["form"]);
      if (Either.isLeft(form)) {
        errors.push({
          itemId: item.id,
          message: `This section's form is invalid: ${TreeFormatter.formatErrorSync(form.left)}`,
        });
        return built;
      }
      return { ...built, form: form.right };
    }
    default:
      warnings.push(
        `widget ${item.id}: unknown contentType ${JSON.stringify(item.contentType)}; omitted`,
      );
      return null;
  }
}

function buildGrid(
  gridId: string,
  gridState: GridState,
  grids: Readonly<Record<string, GridState>>,
  warnings: string[],
  errors: PublishError[],
  resolve?: GridLayoutResolver,
): GridWidgetContent {
  // Layouts resolved from the DOC (breakpoint-independent). `desktop` decouples
  // the published base from whatever breakpoint the canvas happens to be on;
  // `tablet`/`mobile`/`hidden` drive the renderer's `@media` cascade. Absent →
  // store wins.
  const layouts = resolve?.(gridId, gridState.config.columns);
  const desktopOf = (cell: GridCell): GridRect =>
    layouts?.desktop[cell.id] ?? cell.position;
  const cells = Object.values(gridState.items)
    .slice()
    .sort((a, b) => {
      const pa = desktopOf(a);
      const pb = desktopOf(b);
      return pa.y - pb.y || pa.x - pb.x || (a.id < b.id ? -1 : 1);
    });
  const items: GridItem[] = [];
  for (const cell of cells) {
    const content = buildContent(cell, grids, warnings, errors, resolve);
    if (!content) continue;
    const base = desktopOf(cell);
    const rect = (p: GridRect): GridRect => ({
      x: p.x,
      y: p.y,
      w: p.w,
      h: p.h,
    });
    // Record a per-tier layout only when it actually diverges from the base (a
    // hand-placed override, or the mobile auto-stack); a tier that resolves to
    // its base needs no rule — the renderer cascades what's present.
    const differs = (p: GridRect | undefined): p is GridRect =>
      p !== undefined &&
      (p.x !== base.x || p.y !== base.y || p.w !== base.w || p.h !== base.h);
    const tablet = layouts?.tablet[cell.id];
    const mobile = layouts?.mobile[cell.id];
    items.push({
      layout: rect(base),
      ...(differs(tablet) ? { tablet: rect(tablet) } : {}),
      ...(differs(mobile) ? { mobile: rect(mobile) } : {}),
      ...(layouts?.hidden.tablet[cell.id] === true
        ? { hiddenTablet: true }
        : {}),
      ...(layouts?.hidden.mobile[cell.id] === true
        ? { hiddenMobile: true }
        : {}),
      widget: { id: cell.id, content },
    } as unknown as GridItem);
  }
  const gap = gapOf(gridState.config);
  return {
    contentType: "grid",
    columns: gridState.config.columns,
    gapX: gap.x,
    gapY: gap.y,
    // The row height the canvas draws with: the grid's own, or the editor's
    // default when its config has none.
    rowHeight:
      typeof gridState.config.rowHeight === "number"
        ? gridState.config.rowHeight
        : EDITOR_ROW_HEIGHT_PX,
    items,
  };
}

/**
 * Form rules that must hold before a page may go out. Each grid is checked
 * with the form it is part of (a section's own, or none):
 *   - no form inside a form (the contact `form` widget renders its own
 *     <form>; HTML cannot nest them);
 *   - a submit button needs a form to submit;
 *   - field names are unique within a form (a GET would send one of them).
 */
const isIcon = Schema.is(InputIcon);

/**
 * A link a published page may carry: relative (`/about`, `about`, `#top`),
 * or `http(s):`, `mailto:`, `tel:`, `sms:`. Anything else — `javascript:`,
 * `data:` — is refused, however it is cased or spaced: browsers drop tabs
 * and newlines inside a scheme, so those are stripped before it is read.
 * Returns the trimmed link, or null when it must not ship.
 */
export function safeHref(raw: string): string | null {
  const href = raw.trim();
  const compact = [...href]
    .filter((c) => c.charCodeAt(0) > 0x20)
    .join("")
    .toLowerCase();
  const scheme = /^([a-z][a-z0-9+.-]*):/.exec(compact)?.[1];
  if (scheme === undefined) return href;
  return ["http", "https", "mailto", "tel", "sms"].includes(scheme)
    ? href
    : null;
}

/** The link a button or a row item publishes, or null after recording why
 *  it can't be published (one check for both widgets). */
function publishableHref(
  raw: string,
  itemId: string,
  errors: PublishError[],
): string | null {
  const href = safeHref(raw);
  if (href === null)
    errors.push({
      itemId,
      message: `This link (${JSON.stringify(raw.trim().slice(0, 40))}) can't be published: use a page path, #anchor, http(s):, mailto:, tel: or sms:.`,
    });
  return href;
}

const SUBMIT_OUTSIDE_FORM =
  "This submit button isn't in a form. Give its section a form, or make it a link.";

function validateForms(doc: SiteDoc): PublishError[] {
  const errors: PublishError[] = [];
  const visit = (
    grid: GridWidgetContent,
    ownerId: string,
    inForm: boolean,
  ): void => {
    const formHere = grid.form !== undefined;
    const inAForm = inForm || formHere;
    const names = new Map<string, string>();
    for (const { widget } of grid.items) {
      const c = widget.content as unknown as Record<string, unknown>;
      switch (c["contentType"]) {
        case "grid": {
          const g = widget.content as GridWidgetContent;
          if (g.form && inAForm)
            errors.push({
              itemId: widget.id,
              message:
                "A form can't contain another form. Move this section's content out, or remove one of the two forms.",
            });
          visit(g, widget.id, inAForm);
          break;
        }
        case "form":
          if (inAForm)
            errors.push({
              itemId: widget.id,
              message: `This contact form sits inside the form of section "${ownerId}". Move it to a section without a form.`,
            });
          break;
        case "button":
          if (c["type"] === "submit" && !inAForm)
            errors.push({
              itemId: widget.id,
              message: SUBMIT_OUTSIDE_FORM,
            });
          break;
        case "inline-grid":
          visit(
            widget.content as unknown as GridWidgetContent,
            widget.id,
            inAForm,
          );
          break;
        case "input": {
          const name = c["name"];
          if (formHere && typeof name === "string") {
            const other = names.get(name);
            if (other)
              errors.push({
                itemId: widget.id,
                message: `Two fields of this form are named "${name}" (also ${other}). Rename one.`,
              });
            else names.set(name, widget.id);
          }
          break;
        }
      }
    }
  };
  for (const page of doc.pages) visit(page.data.grid, page.id, false);
  const site = doc.site as unknown as {
    header?: GridWidgetContent;
    footer?: GridWidgetContent;
  };
  if (site.header) visit(site.header, "__header", false);
  if (site.footer) visit(site.footer, "__footer", false);
  return errors;
}

export function serializeSiteDoc(input: SerializeInput): SerializeResult {
  const warnings: string[] = [];
  const errors: PublishError[] = [];
  const theme = input.theme ?? DEFAULT_THEME;

  // Home selection — DETERMINISTIC, independent of node input order (the
  // Yjs nodes map order is not guaranteed, and every standalone page is
  // edge-less, so "first edge-less node" alone would be ambiguous). Prefer
  // the canonical "home" node id (DEFAULT_NODES), then the first edge-less
  // node, then the first node.
  const incoming = new Set(input.edges.map((e) => e.target));
  const home =
    input.nodes.find((n) => n.id === "home") ??
    input.nodes.find((n) => !incoming.has(n.id)) ??
    input.nodes[0] ??
    null;

  const used = new Set<string>();
  const slugOf = new Map<string, string>();
  for (const node of input.nodes) {
    let slug: string;
    // The home page keeps `/` unless it has an explicit override; every other
    // page uses its override (slugified) or the label-derived slug, de-duplicated.
    if (home && node.id === home.id && !node.slug && !used.has("/")) {
      slug = "/";
    } else {
      const base = "/" + slugify(node.slug || node.label);
      slug = base;
      let n = 2;
      while (used.has(slug)) slug = `${base}-${n++}`;
    }
    used.add(slug);
    slugOf.set(node.id, slug);
  }

  const pages: Page[] = input.nodes.map((node) => {
    const gridState = input.grids[node.id];
    const grid = gridState
      ? buildGrid(
          node.id,
          gridState,
          input.grids,
          warnings,
          errors,
          input.resolveLayouts,
        )
      : emptyGrid();
    return {
      id: node.id,
      position: { x: node.position.x, y: node.position.y },
      data: {
        slug: slugOf.get(node.id)!,
        title: node.label || "Untitled",
        grid,
        seo: {},
      },
    } as unknown as Page;
  });

  const edges: Edge[] = input.edges.map((e) => {
    const edge: Record<string, unknown> = {
      id: e.id,
      source: e.source,
      target: e.target,
    };
    if (e.label !== undefined) edge["label"] = e.label;
    if (e.menuPosition !== undefined) edge["menuPosition"] = e.menuPosition;
    if (e.hidden) edge["hidden"] = true;
    return edge as unknown as Edge;
  });

  const siteObj: Record<string, unknown> = {
    id: input.siteId,
    name: input.siteName,
    theme,
  };
  if (input.description !== undefined)
    siteObj["description"] = input.description;
  if (input.navNested) siteObj["nav"] = { nested: true };
  // Shared header/footer — the same on every page (see editor sharedRegions).
  const headerState = input.grids[HEADER_GRID_ID];
  const footerState = input.grids[FOOTER_GRID_ID];
  if (headerState)
    siteObj["header"] = buildGrid(
      HEADER_GRID_ID,
      headerState,
      input.grids,
      warnings,
      errors,
      input.resolveLayouts,
    );
  if (footerState)
    siteObj["footer"] = buildGrid(
      FOOTER_GRID_ID,
      footerState,
      input.grids,
      warnings,
      errors,
      input.resolveLayouts,
    );
  const site = siteObj as unknown as Site;

  const doc: SiteDoc = { site, pages, edges };
  errors.push(...validateForms(doc));

  const files: SerializedFile[] = [
    { path: "site.json", content: JSON.stringify({ site, edges }, null, 2) },
    ...pages.map((p) => ({
      path: `pages/${p.id}.json`,
      content: JSON.stringify(p, null, 2),
    })),
  ];

  // Decode what we are about to publish against the schema that defines it.
  //
  // This used to be impossible: the document ids are editor-authored
  // ("n1-welcome"), the schema branded every id as `Schema.UUID`, and so ONE
  // over-strict field disabled validation for the whole document — the header
  // above documented that as a deliberate limitation. The ids now carry a
  // document-id pattern (the brand, which is what actually buys type safety,
  // is unchanged), and the four widget kinds the editor really emits
  // (video/audio/nav/spacer) are declared, so the document is representable.
  //
  // Reported as a warning rather than thrown: this is the first release where
  // the publish path validates at all, and a schema gap should not be able to
  // block a user from publishing. It surfaces through the same channel the
  // omitted-widget warnings already use.
  const decoded = Schema.decodeUnknownEither(SiteDocSchema)(doc, {
    errors: "all",
    onExcessProperty: "ignore",
  });
  if (Either.isLeft(decoded)) {
    warnings.push(
      `published document does not match the SiteDoc schema: ${TreeFormatter.formatErrorSync(
        decoded.left,
      )}`,
    );
  }

  return { doc, files, warnings, errors };
}
